import { useEffect, useMemo, useState } from "react";
import { Avatar, Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography } from "@mui/material";
import { format, parseISO } from "date-fns";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { logisticsTeam } from "constants/logisticsTeam";
import { api } from "services/api.service";
import { useAlert } from "hooks/useAlert";
import { LogisticsTicket, LogisticsTicketDestinationAddress } from "types/logisticsTicket";
import { JobDetailAttachment, JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard, DetailField } from "./DetailCard";
import SiteInformationSummary from "./SiteInformationSummary";

type Props = {
  job: JobDetailViewModel;
  onTicketUpdated?: (ticket: LogisticsTicket) => void;
};

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|heic|svg)$/i;

function isImageAttachment(attachment: JobDetailAttachment) {
  if (attachment.contentType) return attachment.contentType.startsWith("image/");
  return IMAGE_EXTENSIONS.test(attachment.label);
}

function attachmentIcon(attachment: JobDetailAttachment) {
  const type = (attachment.contentType || "").toLowerCase();
  const name = attachment.label.toLowerCase();

  if (type.includes("pdf") || name.endsWith(".pdf")) return PictureAsPdfOutlinedIcon;
  if (type.includes("spreadsheet") || type.includes("excel") || type.includes("csv") || /\.(xlsx?|csv)$/.test(name)) {
    return TableChartOutlinedIcon;
  }
  if (type.includes("word") || type.includes("document") || /\.docx?$/.test(name)) return DescriptionOutlinedIcon;
  return InsertDriveFileOutlinedIcon;
}

function FileAttachmentCard({ attachment }: { attachment: JobDetailAttachment }) {
  const Icon = attachmentIcon(attachment);

  return (
    <Box
      component="a"
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      title={attachment.label}
      sx={{
        width: 160,
        minHeight: 90,
        borderRadius: "8px",
        border: "1px solid #E0E0E0",
        backgroundColor: "#F5F5F5",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        px: 1,
        py: 1,
        textDecoration: "none",
        color: "text.primary",
        flexShrink: 0,
        "&:hover": { backgroundColor: "#EEEEEE" },
      }}
    >
      <Icon sx={{ fontSize: 28, color: "text.secondary" }} />
      <Typography
        variant="caption"
        sx={{
          textAlign: "center",
          wordBreak: "break-word",
          lineHeight: 1.2,
          color: "text.primary",
        }}
      >
        {attachment.label}
      </Typography>
    </Box>
  );
}

function PickupAttachments({ attachments }: { attachments: JobDetailAttachment[] }) {
  if (attachments.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 0.5,
          color: "text.secondary",
        }}
      >
        <AttachFileOutlinedIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary">
          No pickup attachments uploaded
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.5 }}>
      {attachments.map((attachment) =>
        isImageAttachment(attachment) ? (
          <Box
            key={attachment.id ?? attachment.url ?? attachment.label}
            component="a"
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            title={attachment.label}
            sx={{
              width: 120,
              height: 90,
              borderRadius: "8px",
              border: "1px solid #E0E0E0",
              overflow: "hidden",
              display: "block",
              backgroundColor: "#F5F5F5",
              flexShrink: 0,
              "&:hover": { opacity: 0.9 },
            }}
          >
            <Box
              component="img"
              src={attachment.url}
              alt={attachment.label}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        ) : (
          <FileAttachmentCard key={attachment.id ?? attachment.url ?? attachment.label} attachment={attachment} />
        ),
      )}
    </Box>
  );
}

function coordinatorOptions(job: JobDetailViewModel) {
  const options = logisticsTeam.map(([id, name]) => ({ id, name }));
  if (job.coordinatorId && job.coordinatorName && !options.some((option) => option.id === job.coordinatorId)) {
    options.push({ id: job.coordinatorId, name: job.coordinatorName });
  }
  return options;
}

function destinationAddressLabel(address: LogisticsTicketDestinationAddress) {
  const locality = [address.city, address.state].filter(Boolean).join(", ");
  const name = address.name?.trim();
  if (name && locality) return `${name} (${locality})`;
  return name || locality || address.line1 || "Destination";
}

function DestinationField({ job, onTicketUpdated }: Props) {
  const { alertSuccess, alertError } = useAlert();
  const [destinationAddressId, setDestinationAddressId] = useState(
    job.destinationAddressId != null ? String(job.destinationAddressId) : "",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDestinationAddressId(job.destinationAddressId != null ? String(job.destinationAddressId) : "");
  }, [job.destinationAddressId]);

  const destinationLocked = job.destinationAddressLocked;

  const handleChange = (event: SelectChangeEvent<string>) => {
    if (destinationLocked) return;

    const nextId = event.target.value;
    const previousId = destinationAddressId;
    setDestinationAddressId(nextId);
    setSaving(true);

    api
      .patch<LogisticsTicket>(`/logistics_tickets/${job.ticketUuid}/destination_address`, {
        destinationAddressId: nextId ? Number(nextId) : null,
      })
      .then(({ data }) => {
        onTicketUpdated?.(data);
        alertSuccess("Destination updated");
      })
      .catch((error) => {
        setDestinationAddressId(previousId);
        alertError(`Unable to update destination. ${error.response?.data?.errors?.[0] || "Internal server error"}`);
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: "text.secondary",
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        Destination
      </Typography>
      <FormControl size="small" sx={{ minWidth: 300, flex: 1 }}>
        <Select
          displayEmpty
          value={destinationAddressId}
          onChange={handleChange}
          disabled={saving || destinationLocked}
          sx={{
            height: "35px !important",
            fontSize: 14,
            fontWeight: 500,
            backgroundColor: "background.paper",
            "& .MuiSelect-select": {
              py: 0.75,
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <MenuItem value="">Unassigned</MenuItem>
          {job.destinationAddresses.map((address) => (
            <MenuItem key={address.id} value={String(address.id)}>
              {destinationAddressLabel(address)}
            </MenuItem>
          ))}
        </Select>
        {destinationLocked && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Cannot be changed after a vendor request is sent.
          </Typography>
        )}
      </FormControl>
    </Box>
  );
}

function CoordinatorField({ job }: Props) {
  const { alertSuccess, alertError } = useAlert();
  const [coordinatorId, setCoordinatorId] = useState(job.coordinatorId ?? "");
  const [saving, setSaving] = useState(false);
  const options = useMemo(() => coordinatorOptions(job), [job]);
  const selectedName = options.find((option) => option.id === coordinatorId)?.name ?? "";

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextId = event.target.value;
    const previousId = coordinatorId;
    setCoordinatorId(nextId);
    setSaving(true);

    api
      .patch(`/logistics_tickets/${job.ticketUuid}/coordinator`, {
        revivnCoordinatorId: nextId || null,
      })
      .then(() => {
        alertSuccess("Coordinator updated");
      })
      .catch(() => {
        setCoordinatorId(previousId);
        alertError("Unable to update coordinator. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: "text.secondary",
          textTransform: "uppercase",
          mb: 0.5,
        }}
      >
        Coordinator
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {selectedName && (
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: "primary.main",
              flexShrink: 0,
            }}
          >
            {getInitials(selectedName)}
          </Avatar>
        )}
        <FormControl size="small" sx={{ minWidth: 300, flex: 1 }}>
          <Select
            displayEmpty
            value={coordinatorId}
            onChange={handleChange}
            disabled={saving}
            sx={{
              height: "35px !important",
              width: selectedName ? 265 : 300,
              fontSize: 14,
              fontWeight: 500,
              backgroundColor: "background.paper",
              "& .MuiSelect-select": {
                py: 0.75,
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <MenuItem value="">Unassigned</MenuItem>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

export default function JobInformationCard({ job, onTicketUpdated }: Props) {
  return (
    <DetailCard title="Job Information">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
        <DetailField label="Client" value={job.client} />
        <DetailField label="Location" value={job.location} />
        <CoordinatorField job={job} />
        <DestinationField job={job} onTicketUpdated={onTicketUpdated} />
        <DetailField label="Assets" value={`${job.assets} items`} />
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              color: "text.secondary",
              textTransform: "uppercase",
              mb: 0.5,
            }}
          >
            Pickup Date
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary", lineHeight: 1.5 }}>
              {format(parseISO(job.pickupDate), "MMM d, yyyy")}
            </Typography>
            {job.needsAction &&
              (job.cancellationReason ? (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#C2410C", lineHeight: 1.5 }}>
                  Cancelled: {job.cancellationReason}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#C2410C", lineHeight: 1.5 }}>
                  Rescheduled by customer
                </Typography>
              ))}
          </Box>
        </Box>
        <DetailField label="Received" value={job.receivedStatus} />
        <Box sx={{ gridColumn: { md: "1 / -1" } }}>
          <DetailField label="Asset Summary" value={job.assetSummary} />
        </Box>
        <Box sx={{ gridColumn: { md: "1 / -1" } }}>
          <DetailField label="Customer Contact" value={`${job.customerContact} — ${job.customerContactEmail}`} />
        </Box>
        <Box sx={{ gridColumn: { md: "1 / -1" } }}>
          <DetailField label="Site Notes" value={job.siteNotes} />
        </Box>
        {job.siteInformation?.summary && (
          <Box sx={{ gridColumn: { md: "1 / -1" } }}>
            <SiteInformationSummary siteInformation={job.siteInformation} />
          </Box>
        )}
        <Box sx={{ gridColumn: { md: "1 / -1" } }}>
          <DetailField label="Pickup Attachments" value={<PickupAttachments attachments={job.pickupAttachments} />} />
        </Box>
      </Box>
    </DetailCard>
  );
}
